import { memo, useCallback, useState } from 'react';

import { useSelector } from 'react-redux';

import clsx from 'clsx';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';
import { calculateDownPayment } from '@atc/wallet-payment-fns';

import {
    Box,
    Heading,
    Section,
} from 'reaxl';
import { sendClick, sendImpressions } from 'reaxl-analytics';
import {
    inventoryAdditionalProtectionsImpression,
    inventoryHistoryReportImpressions,
    inventoryRecallDetailsClick,
    inventoryRecallImpression,
    inventoryVehicleProtectionImpression,
    inventoryWarrantyDetailClick,
    inventoryWarrantyDetailImpression,
    psxVDPVHRImpression,
} from 'reaxl-analytics-handlers';
import { getAppliedDealerSettings } from 'reaxl-wallet';

import { fallbackInterestRates } from 'axl-config';

import configureExperianTile from '@/utilities/configureExperianTile';

import { authDuck, dealsDuck, interestRateDuck, paymentsDuck, userDuck } from '@/ducks';

import { srpFiltersDuck } from '@/ducks/srp';
import { vdpResultsDuck } from '@/ducks/vdp';

import AdditionalProtectionCard from './AdditionalProtectionCard';
import GridComponent from './GridComponent';
import MoneyBackGuaranteeCard from './MoneyBackGuaranteeCard';
import PsxAdditionalProtectionCard from './PsxAdditionalProtectionCard';
import PsxWarrantyCard from './PsxWarrantyCard';
import PurchaseWithConfidenceModal from './PurchaseWithConfidenceModal';
import SafeGuardCard from './SafeGuardCard';
import SafetyRecallCard from './SafetyRecallCard';
import VhrCard, { getPsxVendor } from './VhrCard';
import WarrantyCard from './WarrantyCard';

function PurchaseWithConfidenceContainer({
    emailCTAProps = {},
    onEmailCTAClick = () => { },
}) {

    const {
        enable_vhr_lead_gate: [enableVhrLeadGate],
        override_experian_tile: [overrideExperianTile],
        peace_of_mind: [, { theme: peaceOfMindTheme }],
        remove_carfax_tile: [removeCarfaxTile],
        vdp_safeguard: [isSafeGuardEnabled],
        vdp_additional_protection: [isAdditionalProtection],
    } = useFeatures([
        'enable_vhr_lead_gate',
        'override_experian_tile',
        'peace_of_mind',
        'remove_carfax_tile',
        'vdp_safeguard',
        'vdp_additional_protection',
    ]);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const {
        warranties: warranty = {},
        owner: {
            hideEmailForm = false,
            privateSeller = false,
        } = {},
        paymentServices: {
            digitalRetailingType = '',
        } = {},
        vhrTiles = [],
        vhrPreview = [],
        id: listingId = '',
        type: listingType = '',
        vin = '',
        moneyBackGuarantee = null,
        returnPolicy = null,
        safeGuardProgram = null,
        safetyRecall = null,
    } = listing ?? {};

    const isDRAccelerate = digitalRetailingType === 'accelerate';

    const dealDetails = useSelector(dealsDuck.selectors.getTransformedDealDetails);
    const existingDeal = useSelector((state) => dealsDuck.selectors.getDealByListingVin(state, vin));
    const nationalRates = useSelector(interestRateDuck.selectors.getDuckState);
    const paymentsInfo = useSelector(paymentsDuck.selectors.getPaymentsInfo);
    const walletInteractions = useSelector((_) => paymentsDuck.selectors.getMyWalletInteractions(paymentsInfo));
    const filterValues = useSelector(srpFiltersDuck.selectors.getRequestValues);
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);

    const [modalDetail, setModalDetail] = useState({
        title: '',
        isShow: false,
        content: '',
        size: '',
        footer: '',
        action: '',
    });

    const isPeaceOfMindThemeInverse = peaceOfMindTheme === 'inverse';

    const { brand } = useBrand();

    const showMoneyBack = returnPolicy?.hasMoneyBackGuarantee || returnPolicy?.hasVehicleReturnPolicy || moneyBackGuarantee;

    const { id: dealXgId } = existingDeal ?? {};
    const isExistingDeal = !!dealXgId;
    const { vehicleProtection: { products, VPPSelectedFlag } = {} } = dealDetails?.[dealXgId] || {};
    const hasVehicleProtection = VPPSelectedFlag && (products?.length > 0);

    const { paymentServices = {}, pricingDetail: { salePrice, msrp } = {} } = listing;
    const { dealerSettings = {} } = paymentServices;
    const price = salePrice || msrp;
    const { interactions: { paymentEmphasis } = {} } = paymentsInfo;

    const {
        defaultLeaseDownPayment,
        downPayment,
        downPaymentPercent,
    } = getAppliedDealerSettings({
        dealerSettings,
        walletInteractions: walletInteractions || {},
        price,
        payments: paymentsInfo,
        nationalRates: nationalRates || fallbackInterestRates,
    });

    const handleSeeDetailsClick = useCallback((event, { warrantyType }) => {
        sendClick(inventoryWarrantyDetailClick, event, { warrantyType, listingType, inventoryId: listingId });
    }, [listingType, listingId]);

    const handleRecallImpression = useCallback(() => {
        sendImpressions({ name: inventoryRecallImpression, data: { listingType, inventoryId: listingId } });
    }, [listingType, listingId]);

    const handleRecallDetailsClick = useCallback((event) => {
        sendClick(inventoryRecallDetailsClick, event, { listingType, inventoryId: listingId });
    }, [listingType, listingId]);

    const handleWarrantyInViewImpressions = useCallback((warrantyType) => {
        sendImpressions({ name: inventoryWarrantyDetailImpression, data: { warrantyType, listingType, inventoryId: listingId } });
    }, [listingType, listingId]);

    const handleProtectionImpression = useCallback(() => {
        sendImpressions({ name: inventoryAdditionalProtectionsImpression, data: { listingType, inventoryId: listingId } });
    }, [listingType, listingId]);

    const handleVehicleProtectionImpression = useCallback(() => {
        sendImpressions({ name: inventoryVehicleProtectionImpression, data: { listingType, inventoryId: listingId } });
    }, [listingType, listingId]);

    const handleVhrInviewImpression = useCallback((tile) => {
        if (privateSeller) {
            sendImpressions({ name: psxVDPVHRImpression, data: { listingId, vendor: getPsxVendor(tile.tileType) } });
        } else {
            const cmp = tile?.tileType?.split('_')[0] === 'CARFAX' ? 'carfaxvhr' : 'autocheck';
            sendImpressions({ name: inventoryHistoryReportImpressions, data: { cmp, inventoryId: listingId } });
        }
    }, [listingId, privateSeller]);

    const { limitedWarranty, vehicleCoverage, originalWarranties = {}, originalWarrantyExpired = true } = warranty ?? {};
    const limitedWarrantyData = limitedWarranty || vehicleCoverage;
    const { hybridElectricComponents, hybridElectricComponentsInCarbState, ...factoryWarranties } = originalWarranties;

    const hasBatteryWarranty = !!hybridElectricComponents || !!hybridElectricComponentsInCarbState;
    const hasLimitedWarranty = !!(limitedWarrantyData && Object.keys(limitedWarrantyData).length > 0);
    const hasFactoryWarranty = !!(factoryWarranties && Object.keys(factoryWarranties).length > 0) && !originalWarrantyExpired;

    const purchaseConfidenceItems = [];

    if (hasFactoryWarranty || hasLimitedWarranty || hasBatteryWarranty) {
        if (privateSeller && hasFactoryWarranty) {
            purchaseConfidenceItems.push(
                <PsxWarrantyCard
                    key="psx_warranty"
                    warranty={originalWarranties}
                    listingId={listingId}
                />
            );
        } else {
            purchaseConfidenceItems.push(
                <WarrantyCard
                    key="warranty"
                    warranty={warranty}
                    handleSeeDetailsClick={handleSeeDetailsClick}
                    handleContentImpression={handleWarrantyInViewImpressions}
                    hasBatteryWarranty={hasBatteryWarranty}
                    hasFactoryWarranty={hasFactoryWarranty}
                    hasLimitedWarranty={hasLimitedWarranty}
                    isSafeGuardEnabled={isSafeGuardEnabled}
                    listingId={listingId}
                    isInverse={isPeaceOfMindThemeInverse}
                />
            );
        }
    }

    const digitalRetailProps = {
        listedPrice: price,
        listing,
        paymentsInfo: {
            ...paymentsInfo,
            dueAtSigning: defaultLeaseDownPayment || calculateDownPayment(price, {
                downPayment: paymentEmphasis ? downPayment : defaultLeaseDownPayment,
                downPaymentPercent: paymentEmphasis ? downPaymentPercent : 0.10,
            }),
        },
        paymentsDuck,
        userDuck,
        isLoggedIn,
    };

    if (safeGuardProgram) {
        purchaseConfidenceItems.push(
            <SafeGuardCard
                key="safeguard"
                safeGuardProgram={safeGuardProgram}
                setModalDetail={setModalDetail}
                handleSeeDetailsClick={handleSeeDetailsClick}
                handleContentImpression={handleWarrantyInViewImpressions}
                listingId={listingId}
                isInverse={isPeaceOfMindThemeInverse}
            />
        );
    }

    if (showMoneyBack) {
        purchaseConfidenceItems.push(
            <MoneyBackGuaranteeCard
                key="mbg"
                returnPolicy={returnPolicy}
                setModalDetail={setModalDetail}
                handleSeeDetailsClick={handleSeeDetailsClick}
                handleContentImpression={handleWarrantyInViewImpressions}
                listingId={listingId}
                isInverse={isPeaceOfMindThemeInverse}
            />
        );
    }

    if (vhrTiles?.length) {
        vhrTiles.forEach((tile, index) => {
            const updatedTile = configureExperianTile(brand, tile);
            purchaseConfidenceItems.push(
                <VhrCard
                    key="vhr"
                    index={index}
                    isInverse={isPeaceOfMindThemeInverse}
                    isPsx={privateSeller}
                    vhrTile={updatedTile}
                    vhrPreview={vhrPreview}
                    listingId={listingId}
                    handleContentImpression={handleVhrInviewImpression}
                    overrideExperianTile={overrideExperianTile}
                    removeVhrCarfaxTile={removeCarfaxTile}
                    {...(enableVhrLeadGate && !hideEmailForm && { onEmailCTAClick })}
                />
            );
        });
    }

    if (privateSeller) {
        purchaseConfidenceItems.push(
            <PsxAdditionalProtectionCard
                key="psx_protections"
                listingId={listingId}
                filterValues={filterValues}
            />
        );
    } else if (isAdditionalProtection && (!isDRAccelerate || !isExistingDeal || !hasVehicleProtection)) {
        purchaseConfidenceItems.push(
            <AdditionalProtectionCard
                key="protections"
                handleContentImpression={handleProtectionImpression}
                handleVehicleProtectionImpression={handleVehicleProtectionImpression}
                listingId={listingId}
                setModalDetail={setModalDetail}
                emailCTAProps={emailCTAProps}
                listingType={listingType}
                onEmailCTAClick={onEmailCTAClick}
                hideEmailForm={hideEmailForm}
                isInverse={isPeaceOfMindThemeInverse}
                {...(isDRAccelerate && { digitalRetailProps })}
            />
        );
    }

    if (safetyRecall) {
        purchaseConfidenceItems.push(
            <SafetyRecallCard
                key="recall"
                safetyRecall={safetyRecall}
                handleRecallDetailsClick={handleRecallDetailsClick}
                handleContentImpression={handleRecallImpression}
                listingId={listingId}
                isInverse={isPeaceOfMindThemeInverse}
            />
        );
    }

    if (!purchaseConfidenceItems.length) {
        return null;
    }

    const mainContent = (
        <>
            <Heading
                componentClass="h3"
                className={clsx({
                    'margin-bottom-4 text-inverse': isPeaceOfMindThemeInverse,
                    'margin-bottom-2': !isPeaceOfMindThemeInverse,
                })}
                level={3}
            >
                Have peace of mind when you buy
            </Heading>
            <GridComponent
                isPsx={privateSeller}
                data-cmp="purchase-confidence-grid-container"
            >
                {purchaseConfidenceItems}
            </GridComponent>
        </>
    );

    return (
        <Section
            alignment="left"
            id="purchaseConfidence"
            className="padding-vertical-4 padding-vertical-md-5"
        >
            {isPeaceOfMindThemeInverse ? (
                <Box
                    backgroundColor="brand-inverse"
                    textColor="inverse"
                    className="padding-5"
                >
                    {mainContent}
                </Box>
            ) : mainContent}
            {modalDetail.isShow
                && (
                    <PurchaseWithConfidenceModal
                        {...modalDetail}
                        setModalDetail={setModalDetail}
                    />
                )}
        </Section>
    );
}

export default memo(PurchaseWithConfidenceContainer);
